/**
 * Voice AI — Settings Page (Orchestrator)
 * Thin parent component: tab bar, toast, delegates to sub-components.
 * Each tab is a sub-component that loads/saves its own data.
 */
import { LitElement, html, css } from 'lit';
import { designSystem, componentStyles } from '../../styles/design-system.js';
import { settingsStyles } from '../../styles/settings-styles.js';
import { iconStyles } from '../../styles/icon-styles.js';
import { llmService } from '../../services/llm-service.js';
import { speechService } from '../../services/speech-service.js';
import { settingsService } from '../../services/settings-service.js';
import './settings-general-tab.js';
import './settings-twilio-tab.js';
import './settings-routing-tab.js';
import './settings-security-tab.js';
import './settings-api-tab.js';
import './settings-llm-tab.js';
import './settings-stt-tts-tab.js';
import './settings-pipeline-tab.js';
import './settings-payment-tab.js';
import './settings-personality-section.js';
import './settings-self-healing-tab.js';
import './settings-setup-tab.js';
import '../voice-modal.js';
import './settings-model-picker.js';

export class SettingsPage extends LitElement {
  static properties = {
    activeTab: { type: String },
    _toastVisible: { type: Boolean, state: true },
    _toastMessage: { type: String, state: true },
    _toastType: { state: true },
    // LLM Providers (passed to llm-settings)
    _llmProviders: { state: true },
    _contextualProviders: { state: true },
    _contextualEnabled: { state: true },
    // Speech Providers (passed to stt-tts-settings)
    _sttProviders: { state: true },
    _ttsProviders: { state: true },
    _showModelPicker: { state: true },
    _modelPickerProvider: { state: true },
    _modelPickerApiKey: { state: true },
    _modelPickerProviderKey: { state: true },
    _modelPickerSearch: { state: true },
  };

  static styles = [
    designSystem,
    componentStyles,
    settingsStyles,
    css`
      :host {
        display: block;
      }

      /* ─── Page Layout ─── */
      .page-header {
        margin-bottom: var(--space-6);
      }

      .page-title {
        font-family: var(--font-display);
        font-size: var(--text-4xl);
        font-weight: var(--font-black);
        color: var(--color-text-primary);
        letter-spacing: -0.04em;
      }

      /* ─── Tab Bar (horizontal) ─── */
      .settings-tabs {
        display: flex;
        gap: var(--space-1);
        border-bottom: 1px solid var(--color-border-primary);
        margin-bottom: var(--space-6);
        overflow-x: auto;
        scrollbar-width: none;
        -ms-overflow-style: none;
      }

      .settings-tabs::-webkit-scrollbar {
        display: none;
      }

      .settings-tab {
        padding: var(--space-3) var(--space-4);
        font-family: var(--font-body);
        font-size: var(--text-sm);
        font-weight: var(--font-medium);
        color: var(--color-text-tertiary);
        cursor: pointer;
        border: none;
        background: transparent;
        border-bottom: 2px solid transparent;
        margin-bottom: -1px;
        transition: all var(--transition-fast);
        white-space: nowrap;
        display: flex;
        align-items: center;
        gap: var(--space-2);
      }

      .settings-tab:hover {
        color: var(--color-text-secondary);
      }

      .settings-tab.active {
        color: var(--color-accent);
        border-bottom-color: var(--color-accent);
        background: var(--color-accent-light);
        border-radius: var(--radius-md) var(--radius-md) 0 0;
      }

      /* ─── Settings Content ─── */
      .settings-content {
        animation: slideUp var(--duration-slow) var(--ease-out);
        max-width: 900px;
      }
      .settings-content.wide {
        max-width: none;
      }

      /* ─── Toast ─── */
      .toast {
        position: fixed;
        bottom: var(--space-6);
        right: var(--space-6);
        background: var(--color-success);
        color: var(--color-text-inverse);
        padding: var(--space-3) var(--space-5);
        border-radius: var(--radius-lg);
        font-family: var(--font-body);
        font-size: var(--text-sm);
        font-weight: var(--font-medium);
        display: flex;
        align-items: center;
        gap: var(--space-2);
        box-shadow: var(--shadow-xl);
        z-index: var(--z-toast);
        transform: translateY(100px);
        opacity: 0;
        transition: all var(--duration-slow) var(--ease-out);
      }

      .toast.visible {
        transform: translateY(0);
        opacity: 1;
      }

      .toast.error {
        background: var(--color-error);
      }

      .toast.testing {
        background: var(--color-accent);
      }
    `
  ];

  constructor() {
    super();
    this.activeTab = 'general';
    this._toastVisible = false;
    this._toastMessage = '';
    this._toastType = 'success';
    this._llmProviders = [];
    this._contextualProviders = [];
    this._contextualEnabled = false;
    this._sttProviders = [];
    this._ttsProviders = [];
    this._showModelPicker = false;
    this._modelPickerProvider = 'all';
    this._modelPickerApiKey = '';
    this._modelPickerProviderKey = '';
    this._modelPickerSearch = '';
  }

  connectedCallback() {
    super.connectedCallback();
    this._loadLlmProviders();
    this._loadSpeechProviders();
  }

  /* ─── LLM Providers ─── */

  async _loadLlmProviders() {
    try {
      const [mainData, ctxData, generalData] = await Promise.all([
        llmService.listProviders(),
        llmService.listContextualProviders(),
        settingsService.loadGeneralConfig(),
      ]);
      if (mainData.success) this._llmProviders = mainData.providers || [];
      if (ctxData.success) this._contextualProviders = ctxData.providers || [];
      if (generalData.success) {
        this._contextualEnabled = generalData.config?.voice_contextual_llm_enabled === '1';
      }
    } catch (err) {
      console.error('[Settings] Failed to load LLM providers:', err);
    }
  }

  async _handleContextualToggle(enabled) {
    try {
      const result = await settingsService.saveGeneralConfig({
        voice_contextual_llm_enabled: enabled ? '1' : '0'
      });
      if (result.success) {
        this._contextualEnabled = enabled;
        this._showToast(enabled ? 'Contextual LLM enabled' : 'Contextual LLM disabled globally');
      } else {
        this._showToast(result.error || 'Failed to update', 'error');
      }
    } catch (err) {
      this._showToast('Network error', 'error');
    }
  }

  _handleLlmEvent(e) {
    const { type } = e;
    if (type === 'provider-saved' || type === 'provider-deleted' || type === 'default-changed' || type === 'contextual-default-changed') {
      this._loadLlmProviders();
    }
    if (type === 'contextual-toggled') {
      this._handleContextualToggle(e.detail.enabled);
    }
    if (type === 'browse-models') {
      this._modelPickerProvider = e.detail.provider || 'all';
      this._modelPickerApiKey = e.detail.apiKey || '';
      this._modelPickerProviderKey = e.detail.providerKey || '';
      this._showModelPicker = true;
    }
    if (type === 'toast') {
      this._showToast(e.detail.message, e.detail.type || 'success');
    }
  }

  _handleModelSelected(e) {
    const llmSettings = this.renderRoot.querySelector('llm-settings');
    if (llmSettings) llmSettings.setModelFromPicker(e.detail.modelId);
    this._showModelPicker = false;
  }

  _closeModelPicker() {
    this._showModelPicker = false;
    this._modelPickerSearch = '';
  }

  _getProviderLabel(providerId) {
    const def = llmService.getProviderDef(providerId);
    return def ? def.name : 'All';
  }

  _renderModelPickerModal() {
    const prov = this._modelPickerProvider || 'all';
    const isAll = prov === 'all' || prov === 'openrouter';
    const label = isAll ? 'All' : this._getProviderLabel(prov);

    return html`
      <voice-modal ?open=${this._showModelPicker} title="Browse ${label} Models" width="720px" height="70vh"
        @modal-close=${this._closeModelPicker}>

        <div slot="sticky" class="model-picker-search" style="padding:var(--space-3) var(--space-4);border-bottom:1px solid var(--color-border-primary);position:relative;">
          <i class="fa-sharp fa-light fa-magnifying-glass" style="position:absolute;left:calc(var(--space-4) + var(--space-3));top:50%;transform:translateY(-50%);color:var(--color-text-tertiary);font-size:var(--text-sm);pointer-events:none;"></i>
          <input type="text" placeholder="Search by name or model ID..."
            style="width:100%;padding:var(--space-2) var(--space-4) var(--space-2) var(--space-10);background:var(--color-bg-secondary);border:1px solid var(--color-border-primary);border-radius:var(--radius-md);color:var(--color-text-primary);font-family:var(--font-body);font-size:var(--text-sm);outline:none;box-sizing:border-box;"
            .value=${this._modelPickerSearch || ''}
            @input=${e => { this._modelPickerSearch = e.target.value; }}
            autofocus
          />
        </div>

        <settings-model-picker
          .provider=${prov}
          .apiKey=${this._modelPickerApiKey || ''}
          .providerKey=${this._modelPickerProviderKey || ''}
          .search=${this._modelPickerSearch || ''}
          @model-selected=${e => this._handleModelSelected(e)}
        ></settings-model-picker>

        <div slot="footer">
          <span style="font-size:var(--text-xs);color:var(--color-text-tertiary);">
            ${isAll ? 'Full model catalog — updated daily' : `${label} models — from provider API`}
          </span>
        </div>
      </voice-modal>
    `;
  }

  /* ─── Speech Providers ─── */

  async _loadSpeechProviders() {
    try {
      const [stt, tts] = await Promise.all([
        speechService.listSttProviders(),
        speechService.listTtsProviders()
      ]);
      if (stt.success) this._sttProviders = stt.providers || [];
      if (tts.success) this._ttsProviders = tts.providers || [];
    } catch (err) {
      console.error('[Settings] Failed to load speech providers:', err);
    }
  }

  _handleSpeechEvent(e) {
    const { type } = e;
    if (type === 'toast') {
      this._showToast(e.detail.message, e.detail.type || 'success');
    } else {
      this._loadSpeechProviders();
    }
  }

  /* ─── Toast ─── */

  _showToast(message, type = 'success') {
    this._toastMessage = message;
    this._toastType = type;
    this._toastVisible = true;
    setTimeout(() => { this._toastVisible = false; }, 4000);
  }

  _handleChildToast(e) {
    this._showToast(e.detail.message, e.detail.type || 'success');
  }

  /* ─── Tab switching ─── */

  _setTab(tab) {
    this.activeTab = tab;
  }

  /* ─── Render ─── */

  render() {
    const tabs = [
      { key: 'general', label: 'General', icon: 'fa-gear' },
      { key: 'twilio', label: 'Twilio', icon: 'fa-phone-office' },
      { key: 'routing', label: 'Phone Routing', icon: 'fa-route' },
      { key: 'llm', label: 'AI Setup', icon: 'fa-brain-circuit' },
      { key: 'pipeline', label: 'Pipeline', icon: 'fa-diagram-project' },
      { key: 'payment', label: 'Payment', icon: 'fa-credit-card' },
      { key: 'security', label: 'Security', icon: 'fa-shield-halved' },
      { key: 'api', label: 'API & Webhooks', icon: 'fa-webhook' },
      { key: 'self-healing', label: 'Self Healing', icon: 'fa-heart-pulse' },
      { key: 'setup', label: 'Setup', icon: 'fa-wand-magic-sparkles' }
    ];

    return html`
      ${iconStyles}
      <div class="page-header">
        <h1 class="page-title">Settings</h1>
      </div>

      <div class="settings-tabs">
        ${tabs.map(tab => html`
          <button
            class="settings-tab ${this.activeTab === tab.key ? 'active' : ''}"
            @click=${() => this._setTab(tab.key)}
          >
            <i class="fa-sharp fa-light ${tab.icon}"></i>
            ${tab.label}
          </button>
        `)}
      </div>

      <div class="settings-content ${this.activeTab === 'self-healing' ? 'wide' : ''}">
        ${this.activeTab === 'general' ? html`
          <general-settings @toast=${this._handleChildToast}></general-settings>
        ` : ''}

        ${this.activeTab === 'twilio' ? html`
          <twilio-settings @toast=${this._handleChildToast}></twilio-settings>
        ` : ''}

        ${this.activeTab === 'llm' ? html`
          <llm-settings
            .providers=${this._llmProviders}
            .contextualProviders=${this._contextualProviders}
            .contextualEnabled=${this._contextualEnabled}
            @provider-saved=${e => this._handleLlmEvent(e)}
            @provider-deleted=${e => this._handleLlmEvent(e)}
            @default-changed=${e => this._handleLlmEvent(e)}
            @contextual-default-changed=${e => this._handleLlmEvent(e)}
            @contextual-toggled=${e => this._handleLlmEvent(e)}
            @browse-models=${e => this._handleLlmEvent(e)}
            @toast=${e => this._handleLlmEvent(e)}
          ></llm-settings>

          <stt-tts-settings
            .sttProviders=${this._sttProviders}
            .ttsProviders=${this._ttsProviders}
            @stt-saved=${e => this._handleSpeechEvent(e)}
            @stt-deleted=${e => this._handleSpeechEvent(e)}
            @stt-default-changed=${e => this._handleSpeechEvent(e)}
            @tts-saved=${e => this._handleSpeechEvent(e)}
            @tts-deleted=${e => this._handleSpeechEvent(e)}
            @tts-default-changed=${e => this._handleSpeechEvent(e)}
            @toast=${e => this._handleSpeechEvent(e)}
          ></stt-tts-settings>

          <personality-settings @toast=${this._handleChildToast}></personality-settings>
        ` : ''}

        ${this.activeTab === 'routing' ? html`
          <routing-settings @toast=${this._handleChildToast}></routing-settings>
        ` : ''}

        ${this.activeTab === 'security' ? html`
          <security-settings @toast=${this._handleChildToast}></security-settings>
        ` : ''}

        ${this.activeTab === 'pipeline' ? html`
          <pipeline-settings @toast=${this._handleChildToast}></pipeline-settings>
        ` : ''}

        ${this.activeTab === 'payment' ? html`
          <payment-settings @toast=${this._handleChildToast}></payment-settings>
        ` : ''}

        ${this.activeTab === 'api' ? html`
          <api-settings @toast=${this._handleChildToast}></api-settings>
        ` : ''}

        ${this.activeTab === 'self-healing' ? html`
          <self-healing-settings @toast=${this._handleChildToast}></self-healing-settings>
        ` : ''}
        ${this.activeTab === 'setup' ? html`
          <settings-setup-tab @toast=${this._handleChildToast}></settings-setup-tab>
        ` : ''}
      </div>

      ${this._renderModelPickerModal()}

      <div class="toast ${this._toastVisible ? 'visible' : ''} ${this._toastType || 'success'}">
        <i class="fa-sharp fa-light ${this._toastType === 'error' ? 'fa-circle-xmark' : 'fa-check'}"></i>
        ${this._toastMessage}
      </div>
    `;
  }
}

customElements.define('settings-page', SettingsPage);
